/**
 * Social Media API
 *
 * API functions for managing contractor social media links
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export type SocialMediaPlatform =
  | 'instagram'
  | 'linkedin'
  | 'facebook'
  | 'twitter'
  | 'youtube'
  | 'tiktok'
  | 'website';

export interface SocialMediaLink {
  id: string;
  contractorId: string;
  platform: SocialMediaPlatform;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaLinkInput {
  platform: SocialMediaPlatform;
  url: string;
}

/**
 * Get my social media links
 */
export async function getMySocialMediaLinks(): Promise<SocialMediaLink[]> {
  const url = buildApiUrl('/contractors/me/social-media');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<SocialMediaLink[]>(response);
}

/**
 * Update social media links (batch upsert)
 * Empty URLs will delete the corresponding link
 */
export async function updateSocialMediaLinks(
  links: SocialMediaLinkInput[]
): Promise<SocialMediaLink[]> {
  const url = buildApiUrl('/contractors/me/social-media');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ links }),
  });

  return handleApiResponse<SocialMediaLink[]>(response);
}

/**
 * Delete a social media link
 */
export async function deleteSocialMediaLink(id: string): Promise<void> {
  const url = buildApiUrl(`/contractors/me/social-media/${id}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  return handleApiResponse<void>(response);
}

/**
 * Get social media links for a contractor by ID (public)
 */
export async function getSocialMediaLinksById(
  contractorId: string
): Promise<SocialMediaLink[]> {
  const url = buildApiUrl(`/contractors/${contractorId}/social-media`);

  const response = await fetch(url, {
    method: 'GET',
  });

  return handleApiResponse<SocialMediaLink[]>(response);
}
